/**
 * MethodList Component
 *
 * Displays list of methods as cards with i18n support
 */

import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { MarkdownContent } from './MarkdownContent';
import type { Method } from '../types';

interface MethodListProps {
  methods: Method[];
  loading: boolean;
  onSelectMethod: (methodId: number) => void;
}

export const MethodList: React.FC<MethodListProps> = ({
  methods,
  loading,
  onSelectMethod,
}) => {
  const { t } = useLanguage();

  if (loading) {
    return (
      <div role="status" aria-live="polite" className="flex items-center justify-center py-20">
        <div className="spinner"></div>
        <span className="ml-3 text-gray-600">{t('loadingMethods')}</span>
      </div>
    );
  }

  if (methods.length === 0) {
    return (
      <div className="text-center py-20">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="mt-4 text-lg text-gray-600">{t('noMethodsFound')}</p>
        <p className="text-sm text-gray-500">{t('tryAdjustingFilters')}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {methods.map((method) => (
        <div
          key={method.id}
          data-testid="method-card"
          tabIndex={0}
          role="button"
          onClick={() => onSelectMethod(method.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectMethod(method.id);
            }
          }}
          className="method-card group"
        >
          {method.thumbnail_url && (
            <img
              src={method.thumbnail_url}
              alt={method.title}
              className="w-full h-48 object-cover rounded-lg mb-4 group-hover:opacity-90 transition-opacity"
            />
          )}

          <h3 className="method-card-title">
            {method.title}
          </h3>

          {/* Excerpt */}
          {method.excerpt && (
            <div className="method-card-excerpt">
              <MarkdownContent content={method.excerpt} />
            </div>
          )}

          {/* Duration */}
          <div className="method-card-duration flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-zinc-700 dark:text-zinc-300">{method.duration_min}-{method.duration_max} min</span>
          </div>

          {/* Module */}
          {method.modules && method.modules.length > 0 && (
            <div className="mb-2">
              <span className="text-xs font-semibold text-zinc-600 dark:text-zinc-400">{t('module')}: </span>
              <span className="text-xs text-zinc-700 dark:text-zinc-300">{method.modules[0].name}</span>
            </div>
          )}

          {/* Activity Types (Tags) */}
          {method.tags && method.tags.length > 0 && (
            <div className="flex gap-1.5 flex-wrap mt-3">
              {method.tags.map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
